// Screen-access catalog. Single source of truth for the per-user permissions
// feature (SuperAdmin grants Employee/Supervisor/Cashier read/write per screen).
//
// Keep the `key` values in sync with the backend CONFIGURABLE_SCREENS list in
// markwave_live_services/routers/users.py.

export type ScreenKey =
  | 'orders'
  | 'roaster'
  | 'users'
  | 'kyc_queue'
  | 'network'
  | 'products'
  | 'offer_settings'
  | 'coupons'
  | 'offers_achieved'
  | 'role_requests'
  | 'asset_values'
  | 'deactivate_user'
  | 'marketing_orders'
  | 'marketing_farm_orders'
  | 'marketing_calls'
  | 'marketing_visits'
  | 'marketing_leads'
  | 'marketing_targets'
  | 'marketing_overview'
  | 'marketing_leaderboard'
  | 'marketing_sales_report'
  | 'marketing_incentives'
  | 'marketing_farm_incentives'
  | 'marketing_farm_leaderboard'
  | 'cashiers';

export type AccessLevel = 'none' | 'read' | 'write';

export interface ScreenDef {
  key: ScreenKey;
  label: string;
  category: string;
  // Route prefixes that belong to this screen. The most specific (longest)
  // matching prefix wins when resolving a pathname to a screen (see screenForPath).
  routes: string[];
}

export interface ScreenCategory {
  name: string;
  color: string; // tailwind text color class used for the category header
  swatch: string; // tailwind bg color class used for the small square
}

export const SCREEN_CATEGORIES: ScreenCategory[] = [
  { name: 'Orders & Sales', color: 'text-emerald-700', swatch: 'bg-emerald-500' },
  { name: 'Users & Network', color: 'text-violet-700', swatch: 'bg-violet-500' },
  { name: 'Catalog & Offers', color: 'text-blue-700', swatch: 'bg-blue-500' },
  { name: 'Farm & Assets', color: 'text-amber-700', swatch: 'bg-amber-500' },
];

// The screens a SuperAdmin can configure per staff user.
export const CONFIGURABLE_SCREENS: ScreenDef[] = [
  { key: 'orders', label: 'Orders', category: 'Orders & Sales', routes: ['/orders'] },
  { key: 'roaster', label: 'Roaster', category: 'Orders & Sales', routes: ['/roaster'] },

  { key: 'users', label: 'Users', category: 'Users & Network', routes: ['/user-management', '/users/customers'] },
  // Investor KYC / bank / unit-nominee verification queue. Was part of Users;
  // an access map saved before the split inherits it from Users (SCREEN_ACCESS_INHERITS).
  { key: 'kyc_queue', label: 'KYC Queue', category: 'Users & Network', routes: ['/kyc-queue'] },
  { key: 'network', label: 'Network', category: 'Users & Network', routes: ['/user-management/network'] },
  { key: 'role_requests', label: 'Role Requests', category: 'Users & Network', routes: ['/role-requests'] },
  { key: 'deactivate_user', label: 'Deactivate User', category: 'Users & Network', routes: ['/deactivate-user'] },

  { key: 'products', label: 'Products', category: 'Catalog & Offers', routes: ['/products'] },
  { key: 'offer_settings', label: 'Offers Mgmt', category: 'Catalog & Offers', routes: ['/offer-settings'] },
  { key: 'coupons', label: 'Coupon Mgmt', category: 'Catalog & Offers', routes: ['/coupons'] },
  { key: 'offers_achieved', label: 'Offers Earned', category: 'Catalog & Offers', routes: ['/offers-achieved'] },

  { key: 'asset_values', label: 'Asset Values', category: 'Farm & Assets', routes: ['/asset-values'] },

  // The cashier roster -- the people who collect cash for an order, and the
  // list their name/phone is picked from on a CASH payment. Filed under people
  // rather than orders because the screen maintains person records; roster
  // cashiers never log in, so this grants access to the DATA, not to an account.
  { key: 'cashiers', label: 'Cashiers', category: 'Users & Network', routes: ['/cashiers'] },
  { key: 'marketing_orders', label: 'My Orders (Marketing)', category: 'Orders & Sales', routes: ['/marketing/my-orders'] },
  { key: 'marketing_farm_orders', label: 'Farm Orders (Marketing)', category: 'Orders & Sales', routes: ['/marketing/farm-orders'] },
  { key: 'marketing_calls', label: 'Call Log (Marketing)', category: 'Orders & Sales', routes: ['/marketing/calls'] },
  { key: 'marketing_visits', label: 'Visits (Marketing)', category: 'Orders & Sales', routes: ['/marketing/visits'] },
  { key: 'marketing_leads', label: 'Leads (Marketing)', category: 'Orders & Sales', routes: ['/marketing/leads'] },
  { key: 'marketing_targets', label: 'Targets (Marketing)', category: 'Orders & Sales', routes: ['/marketing/targets'] },
  { key: 'marketing_overview', label: 'Overview (Marketing)', category: 'Orders & Sales', routes: ['/marketing/overview'] },
  { key: 'marketing_leaderboard', label: 'Leaderboard (Marketing)', category: 'Orders & Sales', routes: ['/marketing/leaderboard'] },
  { key: 'marketing_sales_report', label: 'Sales Report (Marketing)', category: 'Orders & Sales', routes: ['/marketing/sales-report', '/marketing'] },
  { key: 'marketing_incentives', label: 'Incentive Plans (Marketing)', category: 'Orders & Sales', routes: ['/marketing/incentives'] },
  // Farm staff are paid on their own progressive ladder, so their plan and
  // board are separate screens and separately grantable.
  { key: 'marketing_farm_incentives', label: 'Farm Incentives (Marketing)', category: 'Orders & Sales', routes: ['/marketing/farm-incentives'] },
  { key: 'marketing_farm_leaderboard', label: 'Farm Leaderboard (Marketing)', category: 'Orders & Sales', routes: ['/marketing/farm-leaderboard'] },
];

// Screens always available to every logged-in user — never configurable.
export const DEFAULT_SCREENS: { label: string; route: string }[] = [
  { label: 'EMI Calculator', route: '/emi-calculator' },
  { label: 'Unit Calculator', route: '/unit-calculator' },
  { label: 'ACF Calculator', route: '/acf-calculator' },
  { label: 'Referral Page', route: '/referral-landing' },
  { label: 'Live Count', route: '/units-live' },
  { label: 'Privacy & Policy', route: '/privacy-policy' },
  { label: 'Help & Support', route: '/support' },
];

// Granular write sub-actions per screen. Only these screens expose sub-actions;
// every other screen is plain write=all. Keep the keys in sync with the backend
// SCREEN_ACTIONS map in markwave_live_services/routers/users.py.
export interface ScreenAction {
  key: string;
  label: string;
}

export const SCREEN_ACTIONS: Partial<Record<ScreenKey, ScreenAction[]>> = {
  orders: [
    { key: 'edit_orders', label: 'Edit Orders' },
    { key: 'approve_reject', label: 'Approve / Reject' },
    { key: 'create_order', label: 'Create Order' },
    { key: 'delete_order', label: 'Delete Order' },
    { key: 'pending_submission', label: 'Pending Submission (hold before admin approval)' },
    { key: 'show_investor_numbers', label: 'Show Investor Numbers' },
    { key: 'show_finance', label: 'Show Finance' },
  ],
  roaster: [
    { key: 'show_investor_numbers', label: 'Show Investor Numbers' },
  ],
  users: [
    { key: 'add_users', label: 'Add Users' },
    { key: 'edit_users', label: 'Edit Users' },
    { key: 'fill_kyc', label: 'Fill KYC' },
    { key: 'pending_orders', label: 'Pending Orders' },
    { key: 'create_order', label: 'Create New Order' },
    { key: 'update_order', label: 'Update Order' },
    { key: 'edit_order', label: 'Edit Order' },
  ],
  coupons: [
    { key: 'generate_coupons', label: 'Generate Coupons' },
    { key: 'delete_coupons', label: 'Delete Coupons' },
    { key: 'assign_coupons', label: 'Assign Coupons' },
    { key: 'apply_coupons', label: 'Apply Coupons (on orders)' },
    { key: 'remove_coupons', label: 'Remove Coupons (on orders)' },
  ],
};

// Screens added after the Staff Access permission model shipped.
//
// A configured user's screen_access map only holds the keys that existed when a
// SuperAdmin last edited it, and both the nav and the route guard treat an
// absent key as "denied". Every screen added later is therefore invisible AND
// unreachable to everyone already configured — Marketing Managers and plain
// Admins included, since only SuperAdmin is privileged.
//
// For these keys an ABSENT entry falls back to the screen's legacy role
// condition instead of denying. An entry that is present is still obeyed
// exactly, so this can never override a deliberate denial — and a key drops out
// of this list naturally once it has been through a Staff Access save.
export const SCREENS_PENDING_ACCESS_CONFIG: ReadonlySet<string> = new Set([
  'marketing_overview',
  'marketing_leads',
  'marketing_calls',
  'marketing_visits',
  'marketing_targets',
  // The remaining three marketing screens. `marketing_orders` was grantable
  // from the start, so a staff member configured without it lost My Orders
  // outright; sales report and leaderboard only ever existed in this catalog
  // (the backend list did not carry them, so no save could store them) and are
  // now grantable too. Listing all three here means the change cannot take a
  // screen away from anyone already configured — an absent key still falls
  // back to the role, and a present one is still obeyed exactly.
  'marketing_orders',
  'marketing_sales_report',
  'marketing_leaderboard',
  // Brand new — added after this list itself already existed, so it needs the
  // same fallback-to-legacy-role treatment from day one.
  'marketing_incentives',
  // The farm screens, added later still. Every staff member configured before
  // them has no key for either, and an absent key is denied by both the nav
  // and the route guard — so without these two lines the screens would be
  // invisible to exactly the managers who need them.
  'marketing_farm_incentives',
  'marketing_farm_leaderboard',
  // Same reason: every staff member configured before the roster screen existed
  // has no 'cashiers' key at all, and both gates read an absent key as denied.
  'cashiers',
]);

// Screens split out of an existing one. An access map with NO entry for the new
// key reads it as the parent's grant, so a staff member who reached the screen
// through the parent keeps it -- and one who could not, still cannot. An entry
// that is present is obeyed exactly. The Staff Access modal therefore saves an
// explicit 'none' for these keys (the backend keeps it for them only), or
// unticking one would just fall back to the parent again.
export const SCREEN_ACCESS_INHERITS: Partial<Record<ScreenKey, ScreenKey>> = {
  kyc_queue: 'users',
};

/** The access key to read for `key` from `access`: itself, or its parent while unset. */
export function effectiveScreenKey(access: Record<string, string>, key: string): string {
  const parent = SCREEN_ACCESS_INHERITS[key as ScreenKey];
  return parent && !(key in access) ? parent : key;
}

export const SCREEN_BY_KEY: Record<ScreenKey, ScreenDef> = CONFIGURABLE_SCREENS.reduce(
  (acc, s) => {
    acc[s.key] = s;
    return acc;
  },
  {} as Record<ScreenKey, ScreenDef>,
);

// Resolve a pathname to the configurable screen that owns it, choosing the
// longest matching route prefix. Returns null if the path is not gated
// (default screens, detail pages outside any configurable route, etc.).
export function screenForPath(pathname: string): ScreenDef | null {
  let best: ScreenDef | null = null;
  let bestLen = -1;
  for (const s of CONFIGURABLE_SCREENS) {
    for (const r of s.routes) {
      const matches = pathname === r || pathname.startsWith(r + '/');
      if (matches && r.length > bestLen) {
        best = s;
        bestLen = r.length;
      }
    }
  }
  return best;
}
